import styled from 'styled-components';

import { IFeature, IGlassesPackage } from '../../types';
import { PackageNameDisplay } from './PackageNameDisplay';
import { FeatureList } from '../FeatureList';
import { applyDiscount, displayPrice } from '../../utils';
import { breakpoints, colors } from '../../constants';

const PackagePriceWrapper = styled.div``;
const PackagePriceOriginal = styled.span``;
const PackagePriceCurrent = styled.span``;
const PackageDiscountBadge = styled.div``;
const ReadingArea = styled.div``;
const HeaderRow = styled.div``;
const PackagePriceDisplay = styled.div<{ textColor: string }>`
    color: ${({ textColor }) => textColor};
`;

interface IProps {
    features: IFeature[];
    selectedPackage: IGlassesPackage;
    className?: string;
}

const BaseGlassesPackageDisplay = ({
    features,
    selectedPackage,
    className,
}: IProps) => {
    const readingAreaValue =
        !!selectedPackage.readingArea && selectedPackage.readingArea.toString();

    interface IReadingAreaData {
        [key: string]: string;
    }

    // Reading area can have a value from 1-4 (or no value)
    const readingAreaTitle: IReadingAreaData = {
        1: 'Normaal leesgebied',
        2: 'Breed leesgebied',
        3: 'Extra breed leesgebied',
        4: 'Zeer breed leesgebied',
    };

    const imageSrc: IReadingAreaData = {
        1: 'https://grandvision.s3.amazonaws.com/pbe/images/leesgebied_normaal.png',
        2: 'https://grandvision.s3.amazonaws.com/pbe/images/leesgebied_breed.png',
        3: 'https://grandvision.s3.amazonaws.com/pbe/images/leesgebied_extra_breed.png',
        4: 'https://grandvision.s3.amazonaws.com/pbe/images/leesgebied_zeer_breed.png',
    };

    const { priceInt, discountPercentage } = selectedPackage;
    const hasDiscount = !!discountPercentage && discountPercentage > 0;
    const displayedPrice = hasDiscount
        ? applyDiscount(priceInt, discountPercentage)
        : priceInt;

    return (
        <div className={className}>
            <HeaderRow>
                <PackageNameDisplay starAmount={selectedPackage.strength} />
                <PackagePriceWrapper>
                    {hasDiscount && (
                        <PackageDiscountBadge>
                            <span>{discountPercentage}%</span>
                            <small>korting</small>
                        </PackageDiscountBadge>
                    )}
                    <PackagePriceDisplay
                        textColor={selectedPackage.textColorGeneral ?? '#333'}
                    >
                        {hasDiscount && (
                            <PackagePriceOriginal>
                                {displayPrice(priceInt)}
                            </PackagePriceOriginal>
                        )}
                        <PackagePriceCurrent>
                            {displayPrice(displayedPrice)}
                        </PackagePriceCurrent>
                    </PackagePriceDisplay>
                </PackagePriceWrapper>
            </HeaderRow>
            <FeatureList features={features} glassesPackage={selectedPackage} />
            {readingAreaValue && (
                <ReadingArea>
                    <h3>{readingAreaTitle[readingAreaValue]}</h3>
                    <img
                        alt="Breedte leesgebied"
                        src={imageSrc[readingAreaValue]}
                    />
                </ReadingArea>
            )}
        </div>
    );
};

export const GlassesPackageDisplay = styled(BaseGlassesPackageDisplay)`
    ${HeaderRow} {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        margin-top: 60px;

        @media (${breakpoints.mediumMin}) {
            padding-right: 40px;
            padding-bottom: 0;
            margin-top: 0;
            flex-direction: row;
        }
    }

    ${PackagePriceWrapper} {
        position: relative;
    }

    ${PackagePriceDisplay} {
        position: relative;
        width: 100px;
        height: 100px;
        border-radius: 50%;
        background-color: ${colors.white};
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: 'Campton', Arial, Helvetica, sans-serif;
        font-weight: 700;
    }

    ${PackagePriceOriginal} {
        position: absolute;
        top: 18px;
        left: 50%;
        transform: translateX(-50%);
        white-space: nowrap;
        font-size: 1.4rem;
        font-weight: 300;
        color: ${colors.grey};
        text-decoration: line-through;
    }

    ${PackagePriceCurrent} {
        font-size: 2.4rem;
    }

    ${PackageDiscountBadge} {
        position: absolute;
        right: 90px;
        top: -25px;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        background-color: ${colors.orange};
        color: ${colors.white};
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        span {
            font-size: 1.5rem;
            font-weight: 700;
            line-height: 1;
        }

        small {
            font-size: 0.85rem;
            font-weight: 400;
            line-height: 1;
            margin-top: 2px;
        }
    }

    ${ReadingArea} {
        margin-top: 20px;
    }

    h3 {
        color: ${colors.greenDark};
    }

    img {
        width: auto;
        max-width: 120px;
    }

    @media (${breakpoints.mediumMin}) {
        flex: 0 0 45%;

        ${HeaderRow} {
            margin-top: 5px;
        }
    }
`;
